import { auth } from '../firebase';
import { debugLog } from '../utils/debugLog';

const METRICS_ENDPOINT = '/.netlify/functions/revenuecat-metrics';

// Maps RevenueCat metric ids to the values the dashboard cards need.
const METRIC_IDS = [
  'active_trials',
  'active_subscriptions',
  'mrr',
  'revenue',
  'new_customers',
  'active_users',
];

export const fetchRevenueCatMetrics = async (currency = 'USD') => {
  const user = auth.currentUser;
  debugLog('RevenueCat', 'fetch start', {
    signedIn: Boolean(user),
    uid: user?.uid || null,
    email: user?.email || null,
    url: `${METRICS_ENDPOINT}?currency=${encodeURIComponent(currency)}`,
    href: typeof window !== 'undefined' ? window.location.href : null,
  });

  if (!user) {
    throw new Error('Not signed in');
  }
  const idToken = await user.getIdToken();

  const res = await fetch(`${METRICS_ENDPOINT}?currency=${encodeURIComponent(currency)}`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });

  const contentType = res.headers.get('content-type') || '';
  const rawBody = await res.text();
  debugLog('RevenueCat', 'function response', {
    status: res.status,
    contentType,
    bodyPreview: rawBody.slice(0, 400),
  });

  if (!contentType.includes('application/json')) {
    const err = new Error(
      `RevenueCat function did not return JSON (status ${res.status}, content-type ${contentType || 'none'}). Function hit nahi hui — latest code deploy karein ya locally "npx netlify dev" use karein.`
    );
    err.code = 'not_configured';
    throw err;
  }

  let parsed = {};
  try {
    parsed = rawBody ? JSON.parse(rawBody) : {};
  } catch (error) {
    debugLog('RevenueCat', 'JSON parse failed', error?.message);
    const err = new Error('RevenueCat response JSON nahi hai');
    err.code = 'not_configured';
    throw err;
  }

  if (res.status === 501) {
    const err = new Error(
      parsed.message ||
        'RevenueCat is not configured. Netlify env: REVENUECAT_SECRET_KEY and REVENUECAT_PROJECT_ID (Functions scope), then redeploy.'
    );
    err.code = 'not_configured';
    throw err;
  }
  if (!res.ok) {
    throw new Error(parsed.message || `Failed to load RevenueCat metrics (${res.status})`);
  }

  const byId = {};
  (parsed.metrics || []).forEach((metric) => {
    if (METRIC_IDS.includes(metric.id)) {
      byId[metric.id] = metric;
    }
  });

  debugLog('RevenueCat', 'parsed metrics', {
    currency: parsed.currency || currency,
    metricCount: (parsed.metrics || []).length,
    ids: Object.keys(byId),
    values: Object.fromEntries(
      Object.entries(byId).map(([id, metric]) => [id, metric?.value])
    ),
  });

  return { metrics: byId, currency: parsed.currency || currency };
};

export const fetchRevenueCatCustomerSubscriptions = async (uids = []) => {
  const user = auth.currentUser;
  const ids = [...new Set(uids.filter(Boolean))].slice(0, 40);
  if (!user || !ids.length) return {};

  try {
    const idToken = await user.getIdToken();
    const res = await fetch(
      `/.netlify/functions/revenuecat-subscribers?ids=${ids.map(encodeURIComponent).join(',')}`,
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    const contentType = res.headers.get('content-type') || '';
    if (!res.ok || !contentType.includes('application/json')) return {};
    const parsed = await res.json();
    return parsed.subscribers || {};
  } catch (error) {
    debugLog('RevenueCat', 'subscriber lookup failed', error?.message || error);
    return {};
  }
};
